import type z from 'zod/v4'
import { relations, sql } from 'drizzle-orm'
import { index, integer, pgTable, text, timestamp, unique, uuid } from 'drizzle-orm/pg-core'
import { createInsertSchema, createSelectSchema, createUpdateSchema } from 'drizzle-zod'

import { numericAsNumber } from '../../../custom-type'
import { user } from '../../auth'
import { SkillWeight } from '../../content'

// =============================================================================
// EARNED XP DAILY - Lookup table (closed UTC days; today is computed live)
// Stores aggregated earned XP data by user, skillWeightId, and date
// Used for displaying daily XP metrics and aggregations per skill category
// =============================================================================
//
// DEBUGGING NOTES
// ---------------
// Grain: ONE row per (userId, skillWeightId, date-at-start-of-day). There is no
//   per-EarnedXp-record row here — those raw rows are pre-aggregated into this
//   daily lookup table. To find raw inputs for a row, query EarnedXp for the same
//   userId + skillWeightId where the timestamp falls on `date`.
// Formula: `computeEarnedXpDaily` (packages/trigger/src/metrics/earned-xp-daily.ts),
//   bucketed on the learner's day COALESCE(LearnProgress.endedAt, gradedAt, startedAt,
//   EarnedXp.createdAt). `rollupUsers` stores it for closed days by deleting the
//   (users, range) slice and re-inserting it; no `calculatedFor*` flag is involved.
//   => If a past row looks stale, the day was never recomputed: the edit that moved it
//      should have called `recomputePastDays`. The nightly cron covers the last 3 days.
// Consumer: metrics router `getCumulativeEarnedXp` (utils/child-lesson-profile.ts) reads this
//   table up to yesterday and adds today live from `computeEarnedXpDaily` — it SUMs
//   totalXpEarned GROUPED BY skillWeightId over an optional [from, to] date range
//   (from -> UTC start-of-day, to -> UTC end-of-day) and returns one bar per skill
//   for the "Pengalaman Belajar" per-skill XP bars on the child dashboard. Skills
//   with no rows in range show 0 (the router backfills all SkillWeights).

export const EarnedXpDaily = pgTable(
  'earned_xp_daily',
  {
    id: uuid().defaultRandom().primaryKey(), // Surrogate PK; NOT the grain — the (userId, skillWeightId, date) unique key below is the real identity
    userId: text() // Owner of the XP (the child); FK to user.id. ON DELETE CASCADE -> rows vanish when the user is deleted
      .references(() => user.id, { onDelete: 'cascade' })
      .notNull(),
    skillWeightId: uuid() // Skill category this XP belongs to; FK to SkillWeight.id. ON DELETE RESTRICT -> a SkillWeight can't be deleted while rows reference it. This is the group-by key for the dashboard bars
      .references(() => SkillWeight.id, { onDelete: 'restrict' })
      .notNull(),
    date: timestamp({ mode: 'date', withTimezone: true }).notNull(), // The specific date (normalized to start of day) — the compute floors to the UTC day in SQL, so this is midnight UTC of the bucketed day, not a wall-clock instant. Reads compare against UTC start/end of day

    // Daily aggregated metrics per skill
    totalXpEarned: numericAsNumber().notNull().default(0), // Sum of all XP earned for this skill on this day. RAW xp (not divided/scaled) — numericAsNumber maps to numeric(12,2): up to 12 total digits, 2 decimals, parsed to a JS number via parseFloat. Beware float precision when comparing exact values
    recordCount: integer().notNull().default(0), // Number of XP records for this skill on this day = how many source EarnedXp rows were folded into totalXpEarned for this bucket. Integer (count), not XP. recordCount 0 with totalXpEarned 0 means an empty/initialized bucket

    // --- Membership snapshot (the "Laporan" dimension) --------------------------------------
    // The school context the learner was in AT ROLL-UP TIME, stamped by the producer on every write.
    // A report ("Laporan baca kelas 1 A") groups on these, so it keeps meaning the members of 1 A in
    // that period even after a student is moved to another class or promoted to the next grade.
    // Every column is nullable: a child who only belongs to a family organization has no school
    // context. A child can be a member of two organizations at once (family + school), which is
    // exactly why the organization is recorded here instead of being re-derived at read time.
    // NOT FOREIGN KEYS, deliberately. A snapshot must carry no referential dependency at all: even
    // `ON DELETE SET NULL` erases the value the moment a school or a class is deleted, which is the
    // history loss the snapshot exists to prevent. Plain text, resolved by the producer via
    // resolveMetricSnapshotContexts (packages/drizzle/src/queries/metric-snapshot-context.ts).
    // IDS ONLY — no name/avatar columns on this table. Its grain multiplies (user, day) by
    // a SKILL, so repeating a name and two URLs on every row would bloat one of the biggest
    // tables in the set for nothing: a Laporan aggregates from the (user, day)-grain tables, which
    // do carry the labels, and these ids are still enough to filter by school/class and to join
    // back to those rows.
    schoolOrganizationId: text(),
    // The "Kelas" / rombel. Always a team of schoolOrganizationId above.
    schoolTeamId: text(),
    // The "Tingkat" / grade, mirroring user.schoolClass — a plain text column, not a DB enum.
    // No separate label: it already IS the human-readable value, not an id.
    schoolClass: text(),

    // Tracking fields (audit only — none of these participate in the grain or the dashboard read)
    calculatedAt: timestamp({ mode: 'date', withTimezone: true }).defaultNow().notNull(), // When rollupUsers last (re)computed this row's aggregate
    createdAt: timestamp({ mode: 'date', withTimezone: true }).defaultNow().notNull(), // First insert time of this row
    updatedAt: timestamp({ mode: 'date', withTimezone: true }).$onUpdateFn(() => sql`now()`), // Auto-set to now() on a Drizzle .update(). The roll-up deletes and re-inserts, so it stays NULL on stored rows
  },
  (t) => [
    index('idx_earned_xp_daily_user').on(t.userId), // Lookups by a single user
    index('idx_earned_xp_daily_skill_weight').on(t.skillWeightId), // Lookups/group-by per skill category
    index('idx_earned_xp_daily_date').on(t.date), // Date-range scans across all users
    index('idx_earned_xp_daily_user_date').on(t.userId, t.date), // Backs the dashboard read: filter by user, then date range (getCumulativeEarnedXp)
    index('idx_earned_xp_daily_user_skill_date').on(t.userId, t.skillWeightId, t.date), // Same column order as the unique key below
    // Reporting reads: every row a school produced in a period, then narrowed to one class.
    index('idx_earned_xp_daily_school_org_date').on(t.schoolOrganizationId, t.date),
    index('idx_earned_xp_daily_school_team_date').on(t.schoolTeamId, t.date),
    unique('uq_earned_xp_daily_user_skill_date').on(t.userId, t.skillWeightId, t.date), // THE GRAIN. Guarantees at most one row per user+skill+day; the store deletes the (users, range) slice before inserting
  ],
)

// Relations: each daily row belongs to exactly one user and one skillWeight (both required FKs above).
export const earnedXpDailyRelations = relations(EarnedXpDaily, ({ one }) => ({
  user: one(user, {
    fields: [EarnedXpDaily.userId],
    references: [user.id],
  }),
  skillWeight: one(SkillWeight, {
    fields: [EarnedXpDaily.skillWeightId],
    references: [SkillWeight.id],
  }),
}))

// drizzle-zod schemas (derived from the table above — keep them in sync via the table, not by hand).
// Insert: omits id (DB-generated UUID) and createdAt/updatedAt (DB-managed timestamps). Caller still
//   supplies userId, skillWeightId, date, totalXpEarned, recordCount (last two default to 0), and
//   calculatedAt (optional, defaults to now()).
export const InsertEarnedXpDailySchema = createInsertSchema(EarnedXpDaily).omit({
  id: true,
  createdAt: true,
  updatedAt: true,
})

// Update: .partial() (every field optional) and omits the immutable identity columns
//   (id + the userId/skillWeightId/date grain) plus the DB-managed timestamps — i.e. only the
//   aggregate metrics (totalXpEarned, recordCount) and calculatedAt remain updatable.
export const UpdateEarnedXpDailySchema = createUpdateSchema(EarnedXpDaily).partial().omit({
  id: true,
  userId: true,
  skillWeightId: true,
  date: true,
  createdAt: true,
  updatedAt: true,
})

// Select: full row shape as read back from the DB (nothing omitted) — totalXpEarned arrives as a number.
export const SelectEarnedXpDailySchema = createSelectSchema(EarnedXpDaily)

export type InsertEarnedXpDaily = typeof EarnedXpDaily.$inferInsert
export type UpdateEarnedXpDaily = z.infer<typeof UpdateEarnedXpDailySchema>
export type SelectEarnedXpDaily = typeof EarnedXpDaily.$inferSelect
